import React, { useEffect, useRef } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";

interface InternalRulesAProps {
  onDone?: () => void;
  onAdvance?: () => void;
  isBuilderMode?: boolean;
  /** Page ID passed by JourneyPage so the session-storage key is page-scoped. */
  pageId?: number;
}

// InternalRulesA — fires trigger_ruleset_a silently in the background.
//
// BEHAVIOUR (per client request):
//   - Shows a friendly "Fetching available offers" loading screen.
//   - trigger_ruleset_a is called ONCE per page-visit, guarded by BOTH:
//       1. hasFiredRef  — prevents React StrictMode double-mount within a single
//                         page-load (in-memory, reset on unmount).
//       2. sessionStorage key "internalRulesA_fired_<pageId>" — prevents re-firing
//                         on a hard reload of the same page (e.g. after a prefill
//                         gate error blocks advance; without this guard the component
//                         would re-fire on reload and advance the journey past the
//                         gate error the user hasn't fixed yet).
//   - Failures do NOT redirect to the reject page — the journey always advances.
//     (Location serviceability is gated earlier, on the Business Info page, so an
//     out-of-area applicant never reaches this page.)
//   - Builder palette preview is still shown in builder mode.
//
// CRITICAL: The sessionStorage key is cleared by JourneyPage when the journey
// genuinely advances to a NEW page (see JourneyPage.tsx loadPageConfig), so the
// component fires exactly once per page-visit, not once per session.
const InternalRulesA = ({ onDone, onAdvance, isBuilderMode = false, pageId }: InternalRulesAProps) => {
  const advance = onAdvance ?? onDone;
  // In-memory guard: prevents React StrictMode double-mount within a single render cycle
  const hasFiredRef = useRef(false);
  // Session-storage key scoped to this page so reload doesn't re-fire
  const sessionKey = pageId != null ? `internalRulesA_fired_${pageId}` : null;

  useEffect(() => {
    if (isBuilderMode) return;
    if (hasFiredRef.current) return;
    // If we already fired for this page in this browser session (e.g. user reloaded
    // after a prefill gate error), do NOT fire again — the journey stage has not
    // changed, so calling advance() here would skip the gate the user needs to fix.
    if (sessionKey && sessionStorage.getItem(sessionKey)) {
      console.log("[InternalRulesA] Already fired for page", pageId, "— skipping re-fire on reload.");
      return;
    }
    hasFiredRef.current = true;
    if (sessionKey) sessionStorage.setItem(sessionKey, "1");

    const triggerRulesetA = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/trigger_ruleset_a/`, {});
        // Verifications recorded — advance regardless of pass/fail result.
      } catch (err) {
        console.error("[InternalRulesA] trigger_ruleset_a error (non-blocking):", err);
        // Network/server error is also non-blocking — advance the journey.
      } finally {
        if (advance) {
          advance();
        }
      }
    };

    triggerRulesetA();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🔍</div>
        <p className="font-semibold text-gray-500">Verification Set A</p>
        <p className="text-xs mt-1">
          Fires trigger_ruleset_a silently. Failures are non-blocking — journey always advances.
        </p>
      </div>
    );
  }

  // Journey mode: show a friendly loading screen while verifications run in background.
  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50 px-6">
      {/* Animated dots */}
      <div className="flex gap-2 mb-6">
        <span className="w-3 h-3 rounded-full bg-intellend-green animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-3 h-3 rounded-full bg-intellend-green animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-3 h-3 rounded-full bg-intellend-green animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>

      <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
        Fetching available offers for you...
      </h2>
      <p className="text-sm text-gray-500 text-center max-w-xs">
        Please wait while we check your eligibility and match you with the best loan options.
      </p>
    </div>
  );
};

export default InternalRulesA;
